import { useContext, useState } from 'react';

import AppContext from '@/contexts/appContext';
import ChannelSelect from '@/pages/PersonMessaging/components/ChannelSelect';
import EmailComposer from '@/pages/PersonMessaging/components/EmailComposer';
import SmsComposer from '@/pages/PersonMessaging/components/SmsComposer';
import { useMessageSenders } from '@/pages/PersonMessaging/hooks/useMessageSenders';
import { ChannelOption } from '@/pages/PersonMessaging/types';
import BulkMessageRepository from '@/repositories/BulkMessageRepository';
import { Channel } from '@/types/channel';
import { PersonMinimal } from '@/types/person';
import { Alert } from '@/ui/Alert';
import Modal from '@/ui/components/Modal';
import formatPersonName from '@/utils/formatPersonName';
import { htmlToMarkdown } from '@/utils/htmlToMarkdown';

interface Props {
  open: boolean;
  onClose: () => void;
  selectedPeople: PersonMinimal[];
  onSuccess: () => void;
}

const BulkMessageModal = ({ open, onClose, selectedPeople, onSuccess }: Props) => {
  const { addMessage, config } = useContext(AppContext);
  const repo = BulkMessageRepository();
  const attachmentsEnabled = config.message_provider_email === 'SES';

  const selectedIds = selectedPeople.map((p) => p.id);
  const optedOutOfSmsNames = selectedPeople
    .filter((p) => p.channel_sms_enabled === false)
    .map((p) => formatPersonName({ person: p }));

  const { data: allChannelOptions = [] } = useMessageSenders({ hasPhone: true, hasEmail: true });
  const channelOptions = allChannelOptions.filter((o) => o.id !== Channel.VOICE);

  const [selectedOption, setSelectedOption] = useState<ChannelOption | undefined>(undefined);
  const currentOption = selectedOption ?? channelOptions[0];

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [emailComposerKey, setEmailComposerKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChannelChange = (option: ChannelOption) => {
    setSelectedOption(option);
    setBody('');
    setSubject('');
  };

  const handleSubmit = async (bulkMessageId: string | null = null) => {
    if (!currentOption || currentOption.id === Channel.VOICE) return;
    setIsSubmitting(true);
    try {
      await repo.send({
        person_ids: selectedIds,
        channel: currentOption.id,
        sender: currentOption.sender,
        ...(currentOption.id === Channel.EMAIL ? { subject } : {}),
        body: htmlToMarkdown(body),
        ...(bulkMessageId ? { bulk_message_id: bulkMessageId } : {})
      });
      addMessage('success', `Bulk message queued for ${selectedIds.length} recipients`);
      setBody('');
      setSubject('');
      setEmailComposerKey((k) => k + 1);
      onSuccess();
      onClose();
    } catch {
      addMessage('error', 'Failed to send bulk message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Send Message to ${selectedIds.length} ${selectedIds.length === 1 ? 'Person' : 'People'}`}
      restrictCloseToButton={isSubmitting}
      overflowVisible>
      <div className="mt-4 space-y-4">
        <ChannelSelect
          channelOptions={channelOptions}
          selectedOption={currentOption}
          onChange={handleChannelChange}
        />

        {currentOption?.id === Channel.SMS ? (
          <>
            {optedOutOfSmsNames.length > 0 && (
              <Alert variant="error">
                <p>Some recipients have opted out of SMS and will not receive this message</p>
                <p>{optedOutOfSmsNames.join(', ')}</p>
              </Alert>
            )}
            <SmsComposer
              draftMessage={body}
              isLoading={isSubmitting}
              placeholder="Write your message here..."
              replyToMessage={null}
              onChange={setBody}
              onDismissReply={() => {}}
              onSubmit={() => void handleSubmit()}
            />
          </>
        ) : (
          <EmailComposer
            key={emailComposerKey}
            recipientEmail={`${selectedIds.length} recipient${selectedIds.length === 1 ? '' : 's'}`}
            subject={subject}
            body={body}
            isLoading={isSubmitting}
            replyToMessage={null}
            disableAttachments={!attachmentsEnabled}
            onSubjectChange={setSubject}
            onBodyChange={setBody}
            onSend={(bulkMessageId) => void handleSubmit(bulkMessageId)}
            onDiscard={onClose}
            onDismissReply={() => {}}
            onPrepareAttachment={async (file, bulkMessageId) => {
              const result = await repo.prepareAttachment({ file, bulkMessageId });
              return { draft_id: result.bulk_message_id, ...result };
            }}
            onRemoveAttachment={async (bulkMessageId, attachmentId) => {
              await repo.removeAttachment(attachmentId, bulkMessageId);
            }}
          />
        )}
      </div>
    </Modal>
  );
};

export default BulkMessageModal;
